/**
 * KeyboardShortcutsModal - Shows available keyboard shortcuts
 */

import { useEffect } from 'react'
import { X, Keyboard } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from '@/i18n'

interface KeyboardShortcutsModalProps {
  isOpen: boolean
  onClose: () => void
}

interface ShortcutItem {
  keys: string[]
  description: string
}

function KeyCombo({ keys }: { keys: string[] }) {
  return (
    <div className="flex items-center gap-1">
      {keys.map((key, index) => (
        <span key={index}>
          <kbd
            className={clsx(
              'inline-flex items-center justify-center',
              'min-w-[24px] px-1.5 py-0.5',
              'bg-gray-100 dark:bg-dark-accent border border-gray-300 dark:border-gray-600 rounded',
              'text-xs font-mono text-gray-700 dark:text-gray-300'
            )}
          >
            {key}
          </kbd>
          {index < keys.length - 1 && (
            <span className="mx-0.5 text-gray-400 dark:text-gray-500">+</span>
          )}
        </span>
      ))}
    </div>
  )
}

export function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {
  const { t } = useTranslation()

  const shortcuts: { category: string; items: ShortcutItem[] }[] = [
    {
      category: t('chatv2:keyboard_shortcuts.category_general', { defaultValue: 'General' }),
      items: [
        { keys: ['?'], description: t('chatv2:keyboard_shortcuts.show_shortcuts', { defaultValue: 'Show keyboard shortcuts' }) },
        { keys: ['Esc'], description: t('chatv2:keyboard_shortcuts.close_or_stop', { defaultValue: 'Close modal / Stop generating' }) },
        { keys: ['⌘', 'K'], description: t('chatv2:keyboard_shortcuts.new_chat', { defaultValue: 'Start new chat' }) },
        { keys: ['⌘', '/'], description: t('chatv2:keyboard_shortcuts.toggle_sidebar', { defaultValue: 'Toggle sidebar' }) },
      ],
    },
    {
      category: t('chatv2:keyboard_shortcuts.category_messages', { defaultValue: 'Messages' }),
      items: [
        { keys: ['Enter'], description: t('chatv2:keyboard_shortcuts.send_message', { defaultValue: 'Send message' }) },
        { keys: ['Shift', 'Enter'], description: t('chatv2:keyboard_shortcuts.new_line', { defaultValue: 'New line in message' }) },
        { keys: ['⌘', 'C'], description: t('chatv2:keyboard_shortcuts.copy_selected', { defaultValue: 'Copy selected text' }) },
      ],
    },
    {
      category: t('chatv2:keyboard_shortcuts.category_navigation', { defaultValue: 'Navigation' }),
      items: [
        { keys: ['⌘', '↑'], description: t('chatv2:keyboard_shortcuts.scroll_top', { defaultValue: 'Scroll to top' }) },
        { keys: ['⌘', '↓'], description: t('chatv2:keyboard_shortcuts.scroll_bottom', { defaultValue: 'Scroll to bottom' }) },
      ],
    },
  ]

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-dark-card rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-gray-500 dark:text-dark-text-muted" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
              {t('chatv2:keyboard_shortcuts.title', { defaultValue: 'Keyboard Shortcuts' })}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-dark-text-muted" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-6">
            {shortcuts.map((section) => (
              <div key={section.category}>
                <h3 className="text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider mb-3">
                  {section.category}
                </h3>
                <div className="space-y-2">
                  {section.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-1"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {item.description}
                      </span>
                      <KeyCombo keys={item.keys} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-dark-muted border-t border-gray-200 dark:border-dark-border">
          <p className="text-xs text-gray-500 dark:text-dark-text-muted text-center">
            {t('chatv2:keyboard_shortcuts.footer_prefix', { defaultValue: 'Press' })}{' '}
            <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-dark-border rounded text-xs">?</kbd>{' '}
            {t('chatv2:keyboard_shortcuts.footer_suffix', { defaultValue: 'anytime to show this help' })}
          </p>
        </div>
      </div>
    </div>
  )
}

export default KeyboardShortcutsModal
